import React, { useMemo, useRef
 } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { repairPartialMarkdown } from '../../../utils/markdown';

interface MarkdownRendererProps {
  content: string;
  className?: string;
  /** Enable automatic repair of partial markdown (default: true) */
  enableRepair?: boolean;
  /** Show visual indicators for repaired content (default: true) */
  showRepairIndicators?: boolean;
}

export const MarkdownRenderer = ({
  content,
  className,
  enableRepair = true,
  showRepairIndicators = true,
}: MarkdownRendererProps) => {
  const containerRef = useRef<HTMLDivElement>(null);

  // Pre-process content to repair partial markdown
  const processedContent = useMemo(() => {
    if (!enableRepair) {
      return content;
    }

    try {
      const result = repairPartialMarkdown(content, {
        addChunkIndicators: showRepairIndicators,
        preservePartialTables: true,
        strictMode: false,
      });

      return result.repairedContent;
    } catch (error) {
      console.error('Markdown repair failed:', error);
      // Fallback to original content on error
      return content;
    }
  }, [content, enableRepair, showRepairIndicators]);

  const components = useMemo(() => {
    return {
      table: (props: any) => (
        <div className='table-responsive'>
          <table className='table table-sm table-bordered chunk-table'>{props.children}</table>
        </div>
      ),

      // Custom paragraph renderer for repair indicators
      p: ({ children, ...props }: any) => {
        const text = String(children);
        // Style repair indicators differently
        if (
          text.includes('[Partial') ||
          text.includes('[Continued') ||
          text.includes('[Image:') ||
          text.includes('[Code block continues')
        ) {
          return (
            <p className='chunk-indicator text-muted fst-italic' {...props}>
              {children}
            </p>
          );
        }
        return <p {...props}>{children}</p>;
      },

      // Enhance blockquote for continuation indicators
      blockquote: ({ children, ...props }: any) => {
        const text = String(children);
        if (
          text.includes('[Continued from previous chunk]') ||
          text.includes('[Partial table')
        ) {
          return (
            <blockquote className='chunk-continuation-indicator' {...props}>
              {children}
            </blockquote>
          );
        }
        return <blockquote {...props}>{children}</blockquote>;
      },
    };
  }, []);

  return (
    <div ref={containerRef} className={className}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {processedContent}
      </ReactMarkdown>
    </div>
  );
};
